import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import {
  downloadDocument,
  useDeleteDocument,
  useDocuments,
  useUploadDocument,
  type ProjectDocument,
} from '../lib/documents';
import { apiErrorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { ConfirmDialog } from './ConfirmDialog';
import { useAuthStore } from '../lib/authStore';

interface DocumentsPanelProps {
  projectId: number;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function iconForMime(mime: string): string {
  if (mime.startsWith('image/')) return '🖼';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return '📊';
  if (mime.includes('word')) return '📝';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return '📑';
  if (mime.includes('zip')) return '🗜';
  return '📎';
}

export function DocumentsPanel({ projectId }: DocumentsPanelProps) {
  const { user } = useAuthStore();
  const canUpload = user?.role !== undefined;
  const canDelete = user?.role === 'admin' || user?.role === 'project_manager';

  const docsQ = useDocuments(projectId);
  const uploadMut = useUploadDocument(projectId);
  const deleteMut = useDeleteDocument(projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dragging, setDragging] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectDocument | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    for (const file of Array.from(files)) {
      try {
        await uploadMut.mutateAsync(file);
      } catch (err) {
        setUploadError(apiErrorMessage(err));
      }
    }
  };

  const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
    void uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    void uploadFiles(e.dataTransfer.files);
  };

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div className="card-title" style={{ margin: 0 }}>
          Dokumente <span style={{ color: 'var(--color-text-soft)', fontWeight: 400 }}>({(docsQ.data ?? []).length})</span>
        </div>
        {canUpload && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMut.isPending}
          >
            {uploadMut.isPending ? 'Lade hoch …' : '+ Datei wählen'}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleSelect}
      />

      {uploadError && <div className="login-error">{uploadError}</div>}

      {canUpload && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          style={{
            border: '2px dashed',
            borderColor: dragging ? 'var(--color-primary)' : 'var(--color-border-strong)',
            borderRadius: 10,
            padding: 24,
            textAlign: 'center',
            color: 'var(--color-text-soft)',
            fontSize: 13,
            background: dragging ? 'var(--color-planning-bg)' : 'var(--color-surface-alt)',
            marginBottom: 14,
            transition: 'all .15s',
          }}
        >
          {dragging
            ? 'Datei loslassen, um sie hochzuladen.'
            : 'Dateien hierhin ziehen oder oben rechts „Datei wählen“ klicken (max. 25 MB pro Datei).'}
        </div>
      )}

      {docsQ.isLoading ? (
        <div className="loading">Lade Dokumente …</div>
      ) : docsQ.error ? (
        <div className="empty-state"><p>{apiErrorMessage(docsQ.error)}</p></div>
      ) : (docsQ.data ?? []).length === 0 ? (
        <div className="empty-state" style={{ padding: 24 }}>
          <p>Noch keine Dokumente hochgeladen.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th />
                <th>Dateiname</th>
                <th>Grösse</th>
                <th>Hochgeladen</th>
                <th>Von</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(docsQ.data ?? []).map((doc) => (
                <tr key={doc.id}>
                  <td style={{ width: 30, fontSize: 18 }}>{iconForMime(doc.mimetype)}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => downloadDocument(doc)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        fontSize: 13,
                        color: 'var(--color-primary)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontWeight: 600,
                      }}
                    >
                      {doc.original_name}
                    </button>
                    <div style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>{doc.mimetype}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {formatSize(doc.size_bytes)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatDateTime(doc.uploaded_at)}</td>
                  <td>{doc.uploader_name ?? '—'}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => downloadDocument(doc)}
                    >
                      Download
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteTarget(doc)}
                      >
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Dokument löschen"
        danger
        message={
          deleteTarget
            ? `Datei "${deleteTarget.original_name}" endgültig löschen?`
            : ''
        }
        confirmLabel="Löschen"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMut.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
          }
        }}
      />
    </div>
  );
}
